import styled from '@emotion/styled';
import { useMemo } from 'react';
import { Button } from '../button';
import { ClassroomAvatar } from '../classroom-avatar';
import { Tooltip } from '../tooltip';

export type ClassroomAvatarGroupItem = {
  name: string;
  image: string;
};

type Props = {
  classrooms: ClassroomAvatarGroupItem[];
  dataTestId?: string;
  emptyStateText: string;
  isDisabled?: boolean;
  isUpdatePeriod: boolean;
  tooltipText: string;
  onClick: () => void;
};

const AvatarGroupWrapper = styled.div`
  display: flex;

  > div {
    margin-left: 0;

    &:not(:first-of-type) {
      margin-left: -30px;
    }
  }
`;

const CustomButton = styled(Button)`
  padding-right: 8px;
  padding-left: 8px;
`;

const GroupButton = styled(Button)`
  padding: 4px;
  max-height: none;
`;

function ClassroomAvatarGroup({
  classrooms,
  dataTestId,
  isUpdatePeriod,
  emptyStateText,
  tooltipText,
  isDisabled,
  onClick,
}: Props): JSX.Element {
  const tooltipContent = useMemo(() => {
    if (!classrooms) return '';

    if (classrooms.length >= 3) {
      return tooltipText;
    }

    const names = classrooms.map((classroom) => classroom.name).join(', ');

    return names;
  }, [classrooms, tooltipText]);

  const disabled = isUpdatePeriod || isDisabled;

  if (!classrooms?.length)
    return (
      <CustomButton
        dataTestId={dataTestId || 'classroom-avatar-group-empty-state'}
        aria-label={emptyStateText}
        disabled={disabled}
        onClick={() => !disabled && onClick()}
        rightIcon="AddUpIcon"
        role="button"
        variant="tertiary"
      >
        {emptyStateText}
      </CustomButton>
    );

  return (
    <Tooltip
      content={tooltipContent}
      popperOptions={{ strategy: 'fixed' }}
      placement="top"
    >
      <GroupButton
        dataTestId={dataTestId || 'classroom-avatar-group-button'}
        aria-label={emptyStateText}
        variant="tertiary"
        onClick={() => !disabled && onClick()}
        disabled={disabled}
        role="button"
      >
        <AvatarGroupWrapper>
          {classrooms.slice(0, 4).map((classroom) => (
            <ClassroomAvatar avatar={classroom.image} />
          ))}
        </AvatarGroupWrapper>
      </GroupButton>
    </Tooltip>
  );
}

export { ClassroomAvatarGroup };
